/**
 * A plain-English hint for a Graph send failure.
 *
 * WHY: the queue shows exactly what Microsoft said, which is right — the raw error is what you
 * search for and what you quote to anybody who can fix it. But a couple of them are routinely
 * misread, and the cost of misreading is half an hour in the wrong place. The first live attempt
 * to send from another address failed with `ErrorInvalidUser` and was read as a permissions
 * problem; it was not one, and no permission would ever have fixed it.
 *
 * The hint is ADDED, never substituted. Anything not recognised here shows the raw error alone.
 */

/** [test, hint] — the first match wins. */
const HINTS = [
  [
    /ErrorInvalidUser/i,
    'Microsoft could not find a mailbox at that address. A distribution list or mail-enabled group cannot send mail, whatever permissions it has — only a real mailbox can. If you only wanted the name to look different, clear the sending mailbox and set the display name instead.',
  ],
  [
    /ErrorAccessDenied|Access is denied/i,
    'The mailbox exists but this app is not allowed to send as it. Add it to the application access policy in Exchange, then retry — policy changes can take up to 30 minutes to take effect.',
  ],
  [
    /MailboxNotEnabledForRESTAPI/i,
    'That mailbox is not available over the Graph API — usually an on-premises mailbox, or one on a plan without it.',
  ],
  [
    /ErrorSendAsDenied/i,
    'The app may open that mailbox but not send as it. Send-as is a separate permission from access.',
  ],
];

/** @returns {string|null} a sentence to show under the raw error, or null. */
export function mailErrorHint(error) {
  const text = String(error || '');
  if (!text) return null;
  for (const [test, hint] of HINTS) if (test.test(text)) return hint;
  return null;
}

export { HINTS };
